import React from 'react';
import { RuleSet } from 'styled-components';
import { SVGComponent } from '../../Icon/types';

export enum InputSize {
  Large = '54px',
  Medium = '48px'
}

export type InputMode =
  | 'none'
  | 'text'
  | 'tel'
  | 'url'
  | 'email'
  | 'numeric'
  | 'decimal'
  | 'search';

export type InputType =
  | 'text'
  | 'range'
  | 'phone'
  | 'search'
  | 'boldText'
  | 'description';

export type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

export interface ErrorConfig {
  margin?: string | number;
  divider?: boolean;
  setError?: (value: boolean) => void;
}

export interface BaseInputFieldProps {
  name: string;
  stat?: string;
  containerRef?: React.RefObject<HTMLElement>;
  headerRef?: React.RefObject<HTMLElement>;
}

export interface MaskedPhoneInputProps extends BaseInputFieldProps {
  icon?: SVGComponent;
  onClick?: () => void;
  disabled?: boolean;
  placeholder: string;
  showPhoneMask?: boolean;
  cypress?: string;
}

export interface NewInputProps extends Omit<BaseInputFieldProps, 'name'> {
  name?: string;
  id?: string;
  value: string;
  refEl?: React.RefObject<HTMLInputElement | null>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  readOnly?: boolean;
  placeholder: string;
  onBlurHandler?: React.FocusEventHandler<HTMLInputElement>;
  onResetHandler?: () => void;
  onFocusHandler?: () => void;
  onChangeHandler: React.ChangeEventHandler<HTMLInputElement>;
  isPin?: boolean;
  autoComplete?: string;
  inputMode?: InputMode;
  isAutoFocus?: boolean;
  isShowReset?: boolean;
  isInsideModalWithAnimation?: boolean;
  isLabelAnimated?: boolean;
  isShowSearch?: boolean;
  isValueHidden?: boolean;
  size?: InputSize;
  cypress?: string;
  inputTagStyle?: RuleSet<object>;
  isError?: boolean;
  type?: string;
  activeInputStyles?: RuleSet<object>;
}

export type NumberComponentProps = React.InputHTMLAttributes<HTMLInputElement> &
  React.RefAttributes<HTMLInputElement> & {
    component?: string;
    parse?: (val: string) => string;
    format?: ((val: string) => string) | null;
    initialValue?: string;
    'data-stat'?: string;
  };

export interface NumberFieldProps extends BaseInputFieldProps {
  placeholder?: string;
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  onReset?: () => void;
  isFullWidth?: boolean;
  floatingPlaceholder?: string;
  onFocusHandler?: React.FocusEventHandler<HTMLInputElement>;
  onBlurHandler?: React.FocusEventHandler<HTMLInputElement>;
  onPasteHandler?: React.ClipboardEventHandler<HTMLInputElement>;
  isCustomInput?: boolean;
  refEl?: React.RefObject<HTMLInputElement | null>;
}

export interface TextAreaFieldProps extends BaseInputFieldProps {
  format?: (value: string) => string;
  onClick?: () => void;
  inputMode?: InputMode;
  placeholder: string;
  maxLength?: string;
  refEl?: React.RefObject<HTMLTextAreaElement | null>;
}

export interface TextFieldProps extends BaseInputFieldProps {
  cypress?: string;
  icon?: SVGComponent;
  value?: number | string;
  format?: (value: string | number) => string;
  onClick?: () => void;
  disabled?: boolean;
  inputMode?: InputMode;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string,
    isNum: boolean,
    isFloat: boolean
  ) => void;
  maxLength?: string;
  placeholder: string | React.ReactNode;
  isValueHidden?: boolean;
  isFloat?: boolean;
  styleType?: 'default' | 'bold';
  type?: string;
  formValues?: Record<string, string>;
  isEnabledAutoSelectionRange?: boolean;
  endAdornment?: React.ReactNode;
}

export interface InputProps {
  type: InputType;
  endAdornment?: React.ReactNode;
  hasErrorField?: boolean;
  errorConfig?: ErrorConfig;
  id?: string;
  stat?: string;
  name?: string;
  icon?: IconComponent;
  value?: string | number;
  format?: (value: string) => string;
  refEl?: React.RefObject<HTMLInputElement | null>;
  isFloat?: boolean;
  onReset?: () => void;
  onClick?: () => void;
  disabled?: boolean;
  maxLength?: string;
  placeholder?: string | React.ReactNode;
  onBlurHandler?: React.FocusEventHandler<HTMLInputElement>;
  onFocusHandler?: () => void;
  inputMode?: InputMode;
  isValueHidden?: boolean;
  isEnabledAutoSelectionRange?: boolean;
  containerRef?: React.RefObject<HTMLElement>;
  headerRef?: React.RefObject<HTMLElement>;
  isFullWidth?: boolean;
  floatingPlaceholder?: string;
  isCustomInput?: boolean;
  onPasteHandler?: React.ClipboardEventHandler<HTMLInputElement>;
  isPin?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  isShowReset?: boolean;
  isAutoFocus?: boolean;
  onResetHandler?: () => void;
  onChangeHandler?: React.ChangeEventHandler<HTMLInputElement>;
  isInsideModalWithAnimation?: boolean;
  isShowSearch?: boolean;
  isLabelAnimated?: boolean;
  readOnly?: boolean;
  showPhoneMask?: boolean;
  onChange?:
    | ((
        e: React.ChangeEvent<HTMLInputElement>,
        name: string,
        isNum: boolean,
        isFloat: boolean
      ) => void)
    | ((value: string) => void);
  formValues?: Record<string, string>;
  cypress?: string;
}
